import type {
	API_CreatePlatformCheckoutRequest,
	API_CreatePlatformCheckoutResponse,
	API_GetLLMUsageResponse,
	API_GetPlatformPlansResponse,
	API_GetPlatformPortalResponse,
	API_GetPlatformSubscriptionHistoryResponse,
	API_GetPlatformSubscriptionResponse,
	API_GetPlatformUsageResponse,
	API_GetTrialStatusResponse,
} from "@pilatestime/shared-types";
import { queryOptions } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useMutationWithToast } from "@/hooks/useMutationWithToast";
import api from "./api_client";

// ============================================
// API Functions
// ============================================

/** Fetch plans (requires authentication) */
export const fetchPlatformPlans = (): Promise<API_GetPlatformPlansResponse> =>
	api.get("/platform-billing/plans").then((res) => res.data);

/** Fetch plans (public, no authentication required) */
export const fetchPublicPlatformPlans =
	(): Promise<API_GetPlatformPlansResponse> =>
		api.get("/platform-billing/public-plans").then((res) => res.data);

export const fetchPlatformSubscription = (
	organizationId: string,
): Promise<API_GetPlatformSubscriptionResponse> =>
	api
		.get(`/organizations/${organizationId}/platform-billing/subscription`)
		.then((res) => res.data);

export const fetchPlatformUsage = (
	organizationId: string,
): Promise<API_GetPlatformUsageResponse> =>
	api
		.get(`/organizations/${organizationId}/platform-billing/usage`)
		.then((res) => res.data);

export const fetchPlatformHistory = (
	organizationId: string,
): Promise<API_GetPlatformSubscriptionHistoryResponse> =>
	api
		.get(`/organizations/${organizationId}/platform-billing/history`)
		.then((res) => res.data);

export const fetchTrialStatus = (
	organizationId: string,
): Promise<API_GetTrialStatusResponse> =>
	api
		.get(`/organizations/${organizationId}/platform-billing/trial-status`)
		.then((res) => res.data);

export const fetchLLMUsage = (
	organizationId: string,
): Promise<API_GetLLMUsageResponse> =>
	api.get(`/organizations/${organizationId}/llm/usage`).then((res) => res.data);

export const createPlatformCheckout = (
	organizationId: string,
	data: API_CreatePlatformCheckoutRequest,
): Promise<API_CreatePlatformCheckoutResponse> =>
	api
		.post(`/organizations/${organizationId}/platform-billing/checkout`, data)
		.then((res) => res.data);

export const getPlatformPortal = (
	organizationId: string,
): Promise<API_GetPlatformPortalResponse> =>
	api
		.post(`/organizations/${organizationId}/platform-billing/portal`, {})
		.then((res) => res.data);

// ============================================
// Query Options
// ============================================

/** Query options for fetching plans (requires authentication) */
export const fetchPlatformPlansQueryOptions = () =>
	queryOptions({
		queryKey: ["platform_plans"],
		queryFn: () => fetchPlatformPlans(),
		staleTime: 10 * 60 * 1000, // 10 minutes - plans don't change often
	});

/** Query options for fetching plans (public, no authentication required) */
export const fetchPublicPlatformPlansQueryOptions = () =>
	queryOptions({
		queryKey: ["platform_plans_public"],
		queryFn: () => fetchPublicPlatformPlans(),
		staleTime: 10 * 60 * 1000, // 10 minutes - plans don't change often
	});

export const fetchPlatformSubscriptionQueryOptions = (
	userId: string,
	organizationId: string,
) =>
	queryOptions({
		queryKey: ["platform_subscription", organizationId, userId],
		queryFn: () => fetchPlatformSubscription(organizationId),
		staleTime: 2 * 60 * 1000, // 2 minutes
	});

export const fetchPlatformUsageQueryOptions = (
	userId: string,
	organizationId: string,
) =>
	queryOptions({
		queryKey: ["platform_usage", organizationId, userId],
		queryFn: () => fetchPlatformUsage(organizationId),
		staleTime: 2 * 60 * 1000, // 2 minutes
	});

export const fetchPlatformHistoryQueryOptions = (
	userId: string,
	organizationId: string,
) =>
	queryOptions({
		queryKey: ["platform_history", organizationId, userId],
		queryFn: () => fetchPlatformHistory(organizationId),
		staleTime: 5 * 60 * 1000, // 5 minutes
	});

export const fetchTrialStatusQueryOptions = (
	userId: string,
	organizationId: string,
) =>
	queryOptions({
		queryKey: ["trial_status", organizationId, userId],
		queryFn: () => fetchTrialStatus(organizationId),
		staleTime: 2 * 60 * 1000, // 2 minutes
	});

export const fetchLLMUsageQueryOptions = (
	userId: string,
	organizationId: string,
) =>
	queryOptions({
		queryKey: ["llm_usage", organizationId, userId],
		queryFn: () => fetchLLMUsage(organizationId),
		staleTime: 2 * 60 * 1000, // 2 minutes
	});

// ============================================
// Mutation Hooks
// ============================================

export const useCreatePlatformCheckout = () => {
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			organizationId,
			data,
		}: {
			organizationId: string;
			data: API_CreatePlatformCheckoutRequest;
		}) => createPlatformCheckout(organizationId, data),
		toastMessages: {
			loading: t("platformBilling.checkout.loading"),
			success: t("platformBilling.checkout.success"),
			error: t("platformBilling.checkout.error"),
		},
	});
};

export const useGetPlatformPortal = () => {
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({ organizationId }: { organizationId: string }) =>
			getPlatformPortal(organizationId),
		toastMessages: {
			loading: t("platformBilling.portal.loading"),
			success: t("platformBilling.portal.success"),
			error: t("platformBilling.portal.error"),
		},
	});
};
